import { Tooltip } from "@progress/kendo-react-tooltip";
import { Info as InfoIcon } from "lucide-react";
import * as React from "react";
import { CSSProperties } from "react";
import { renderTooltipText } from "@/components/Kendo/ToolTip/renderTooltipText";

const IconHint: React.FC<{
  hint: string;
  style?: CSSProperties;
  iconWidth?: number;
  // 14px is the approved information-icon size (QA-19 / QA-27). Several callers
  // were already passing `iconWidth={14}` explicitly to work around the old
  // 20px default, which is what left icon sizes inconsistent between forms.
}> = ({ hint, style = {}, iconWidth = 14 }) => {
  // Two layouts, chosen by the caller. Overlay (the default) floats the icon
  // inside the control's right edge; inline lets it sit in a flex/text row —
  // AttributeColumn and RemoteUserAttributeForm opt in with `position: "unset"`.
  //
  // The overlay offsets must be gated rather than merely overridable: `style`
  // is spread last, so `position: "unset"` cancels `position` and `top` but
  // NOT `transform`, leaving `translateY(-50%)` to pull the icon 7px above its
  // row. Emitting them only in overlay mode means an inline caller can't
  // inherit half a layout.
  const overlay = style.position === undefined;

  return (
    <Tooltip
      anchorElement="target"
      openDelay={300}
      parentTitle={true}
      style={{ maxWidth: 400 }}
      showCallout={false}
      content={renderTooltipText}
    >
      <span
        title={hint}
        role="button"
        style={{
          ...{
            ...(overlay
              ? {
                  position: "absolute" as const,
                  right: "0.625rem",
                  // Half the 46px control, NOT 50%. The positioning ancestor is
                  // `.k-form-field-wrap`, which also holds <Hint> and <Error> —
                  // so a percentage resolves against the taller wrapper and, the
                  // moment a validation error renders, drops this icon below the
                  // input's centre line and over the error text. The control is
                  // the wrapper's first child, so half its height lands on its
                  // centre in every state. Matches `.tlx-file-input-wrapper` in
                  // Forms/style.scss.
                  top: "1.4375rem",
                  transform: "translateY(-50%)",
                }
              : {}),
            // `inline-flex`, not `flex`: inline callers pass
            // `verticalAlign: "middle"`, which a block-level box ignores. An
            // absolutely positioned box is blockified either way, so overlay
            // mode is unaffected.
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            // Pin the box to the glyph. Measured, this span was rendering 25px
            // wide around a 14px icon (inherited line-height/padding), which is
            // what made the copy button overlap it by 6px however the offsets
            // were tuned — the arithmetic assumed a 14px box.
            boxSizing: "border-box",
            width: iconWidth,
            height: iconWidth,
            padding: 0,
            lineHeight: 1,
            marginRight: "0.3125rem",
            // `--tlx-color-tertiary` was never defined in either theme, so this
            // resolved to nothing and the icon inherited the field's text
            // colour. `--tlx-icon-link` is the dedicated icon intent (#00A8CF). It
            // is declared once on :root in _semantic-light.scss and inherits
            // into dark, where the same value applies.
            color: "var(--tlx-icon-link)",
            zIndex: 1,
          },
          ...style,
        }}
      >
        <InfoIcon width={iconWidth} height={iconWidth} />
      </span>
    </Tooltip>
  );
};

export default IconHint;
